import React, { useState } from 'react';
import { Calculator, AlertCircle, Loader2, Phone, ArrowRight, Clock, DollarSign, Users, Wrench } from 'lucide-react';
import { getProjectEstimate } from '../lib/openai';

export function ProjectEstimator() {
  const [projectDescription, setProjectDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [estimate, setEstimate] = useState<{
    laborHours: number;
    materialCost: number;
    teamSize: number;
    totalCost: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateEstimate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getProjectEstimate({
        service: 'General Project',
        description: projectDescription,
        hasMaterials: false,
        projectSize: 'Medium',
        timeline: 'Within 3-6 Months',
        propertyType: 'Single Family Home'
      });
      setEstimate(result);
    } catch (error) {
      console.error('Error calculating estimate:', error);
      setError('Unable to generate estimate. Call (914) 473-3775) for Estimate');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-b from-zinc-800/30 to-zinc-900/30 rounded-2xl overflow-hidden shadow-2xl border border-zinc-800/50">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-yellow-400/10 via-yellow-400/5 to-transparent border-b border-yellow-400/20 p-8 sm:p-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-yellow-400/10 p-3 rounded-xl">
              <Calculator className="h-8 w-8 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-light text-zinc-100">Project Estimate Calculator</h2>
              <p className="text-zinc-400 text-sm mt-1">Get an instant AI-powered estimate for your project</p>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-yellow-400/5 border border-yellow-400/20 rounded-xl p-4">
            <Phone className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-zinc-300 text-sm leading-relaxed">
                For the most accurate estimate, call{' '}
                <a href="tel:9149606433" className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium">
                  (914) 960-6433
                </a>
              </p>
              <p className="text-zinc-500 text-sm">Available Monday - Friday, 7 AM - 7 PM EST</p>
            </div>
          </div>
        </div>

        <div className="p-8 sm:p-10 space-y-8">
          {/* Input Section */}
          <div className="space-y-4">
            <label htmlFor="description" className="block text-sm text-zinc-400">
              Project Description
            </label>
            <textarea
              id="description"
              rows={6}
              className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl px-4 py-3 text-zinc-200 placeholder:text-zinc-500 focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 transition-all duration-200"
              placeholder="Please provide detailed information about your project. For example:
• Type of project (bathroom remodel, kitchen renovation, etc.)
• Scope of work (what needs to be done)
• Room dimensions or size
• Special requirements or preferences"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
            ></textarea>

          </div>

          {error && (
            <div className="flex items-center gap-3 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl p-4">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={calculateEstimate}
            disabled={!projectDescription || isLoading}
            className="w-full bg-yellow-400 text-zinc-900 px-6 py-4 rounded-xl text-sm tracking-wider hover:bg-yellow-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Calculating Estimate...
              </>
            ) : (
              <>
                <Calculator className="h-5 w-5" />
                Calculate Estimate
              </>
            )}
          </button>

          {/* Results Section */}
          {estimate && (
            <div className="mt-8 space-y-6 animate-fade-in">
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <h3 className="text-xl text-zinc-100">Project Estimate</h3>
                <span className="text-sm text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full">AI-Generated</span>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-5 w-5 text-yellow-400" />
                    <div className="text-yellow-400 text-sm">Labor Hours</div>
                  </div>
                  <div className="text-2xl text-zinc-100 mb-2">{estimate.laborHours} hours</div>
                  <div className="text-zinc-400 text-sm">
                    Estimated duration
                  </div>
                </div>

                <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="h-5 w-5 text-yellow-400" />
                    <div className="text-yellow-400 text-sm">Team Size</div>
                  </div>
                  <div className="text-2xl text-zinc-100 mb-2">{estimate.teamSize} people</div>
                  <div className="text-zinc-400 text-sm">
                    Including specialists
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
                <div className="flex items-center gap-3 mb-4">
                  <Wrench className="h-5 w-5 text-yellow-400" />
                  <div className="text-yellow-400 text-sm">Materials & Equipment</div>
                </div>
                <div className="text-2xl text-zinc-100 mb-2">${estimate.materialCost.toLocaleString()}</div>
                <div className="text-zinc-400 text-sm">
                  Materials and supplies
                </div>
              </div>

              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-5 w-5 text-zinc-900" />
                  <div className="text-zinc-900 text-sm font-medium">Total Estimate</div>
                </div>
                <div className="text-3xl text-zinc-900 mb-2">${estimate.totalCost.toLocaleString()}</div>
                <div className="text-zinc-900/75 text-sm">
                  Professional estimate
                </div>
              </div>

              <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
                <div className="flex items-start gap-3 mb-6">
                  <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      This is an AI-generated estimate and may not reflect the final cost of your project. 
                      Actual costs can vary based on specific requirements, material choices, and site conditions.
                    </p>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      For the most accurate pricing tailored to your specific needs, please contact us directly.
                    </p>
                  </div>
                </div>
                <a 
                  href="tel:9149606433"
                  className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors group"
                >
                  <Phone className="h-5 w-5" />
                  <span className="text-sm font-medium">Call (914) 960-6433 for accurate estimate</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}